import { Card } from '@astryxdesign/core/Card';
import { Heading } from '@astryxdesign/core/Heading';
import { HStack } from '@astryxdesign/core/HStack';
import { Link } from '@astryxdesign/core/Link';
import { Text } from '@astryxdesign/core/Text';
import { VStack } from '@astryxdesign/core/VStack';
import { MapPin, Phone } from '@phosphor-icons/react';
import type { Studio } from '@/api/schemas';
import { dayLabel, fmtPhoneHref, weekdayShort } from '@/lib/format';

function isoWeekday(isoDate: string): number {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay() || 7;
}

export function Contacts({ studio }: { studio: Studio }) {
  const s = studio.settings;
  const todayDow = isoWeekday(studio.today);
  const byDay = new Map<number, string[]>();
  for (const w of studio.workingHours) byDay.set(w.weekday, [...(byDay.get(w.weekday) ?? []), `${w.opens}–${w.closes}`]);
  const closedSoon = studio.exceptions.filter((e) => e.isClosed).slice(0, 3);

  return (
    <VStack gap={3} as="section" aria-labelledby="contacts-heading" id="contacts">
      <Heading level={2} id="contacts-heading">
        Как нас найти
      </Heading>
      <Card>
        <VStack gap={4}>
          <HStack gap={2} vAlign="start">
            <MapPin size={20} aria-hidden className="shrink-0 text-accent" />
            <VStack gap={1}>
              <Text>{s.address}</Text>
              {s.mapUrl && (
                <Link href={s.mapUrl} isExternalLink>
                  Открыть на карте
                </Link>
              )}
            </VStack>
          </HStack>
          <HStack gap={2} vAlign="center">
            <Phone size={20} aria-hidden className="shrink-0 text-accent" />
            <Link href={fmtPhoneHref(s.phone)}>{s.phone}</Link>
          </HStack>
          <VStack gap={1} as="dl" aria-label="Время работы">
            {[1, 2, 3, 4, 5, 6, 7].map((d) => (
              <HStack key={d} gap={2} justify="between" className={d === todayDow ? 'hours-row hours-row--today' : 'hours-row'}>
                <Text as="span" weight={d === todayDow ? 'semibold' : undefined}>
                  {weekdayShort(d)}
                  {d === todayDow ? ' · сегодня' : ''}
                </Text>
                <Text as="span" color={byDay.has(d) ? 'primary' : 'secondary'} hasTabularNumbers>
                  {byDay.get(d)?.join(', ') ?? 'выходной'}
                </Text>
              </HStack>
            ))}
          </VStack>
          {closedSoon.length > 0 && (
            <Text type="supporting">
              Не работаем: {closedSoon.map((e) => `${dayLabel(e.day, studio.today, studio.timezone)}${e.note ? ` (${e.note})` : ''}`).join(', ')}
            </Text>
          )}
        </VStack>
      </Card>
    </VStack>
  );
}
